import '../Submission.css';
import { useState, useEffect }  from 'react';
import ExifReader from 'exifreader';

function SkateparkInputForm(props){

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        id: '',
        builder: '',
        sqft: '',
        lights: '',
        covered: '',
        url: '',
        elements: '',
        pinimage: '',
        group: ''
    });

    const [photos, setPhotos] = useState([]);
    const [submissionStatus, setSubmissionStatus] = useState("New Submission");
    const [uniquePin, setUniquePin] = useState([]);
    const [uniqueLocationGroup, setLocationGroup] = useState([]);
    const [showOptionalFields, setShowOptionalFields] = useState(false);

    // New state for submissions table
    const [submissions, setSubmissions] = useState([]);
    const [submissionsLoading, setSubmissionsLoading] = useState(false);
    const [submissionsError, setSubmissionsError] = useState(null);
    const [showSubmissions, setShowSubmissions] = useState(false);

    useEffect(() => {
        // Function to populate dropdowns
        const populateDropdowns = () => {
            if (props.fileListingArray && props.fileListingArray.length > 0) {
                const uniqueInitialPins = [...new Set(props.fileListingArray.map(item => item.pinimage))].filter(Boolean);
                uniqueInitialPins.sort();
                setUniquePin(uniqueInitialPins);

                const uniqueLocationGroups = [...new Set(props.fileListingArray.map(item => item.group))].filter(Boolean);
                uniqueLocationGroups.sort();
                setLocationGroup(uniqueLocationGroups);
            }
        };

        // Try immediately
        populateDropdowns();

        // Also try after a short delay in case data loads later
        const timer = setTimeout(populateDropdowns, 1000);

        fetchSubmissions();

        return () => clearTimeout(timer);
    }, [props.fileListingArray]);

// Also watch for changes to props.fileListingArray
    useEffect(() => {
        if (props.fileListingArray && props.fileListingArray.length > 0) {
            const uniqueInitialPins = [...new Set(props.fileListingArray.map(item => item.pinimage))].filter(Boolean);
            uniqueInitialPins.sort();
            setUniquePin(uniqueInitialPins);

            const uniqueLocationGroups = [...new Set(props.fileListingArray.map(item => item.group))].filter(Boolean);
            uniqueLocationGroups.sort();
            setLocationGroup(uniqueLocationGroups);
        }
    }, [props.fileListingArray]);

    // Function to fetch submissions from your server
    const fetchSubmissions = async () => {
        setSubmissionsLoading(true);
        setSubmissionsError(null);

        try {
            const response = await fetch(process.env.REACT_APP_LIST_SUBMISSIONS_API_URL, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setSubmissions(data.submissions || []);
        } catch (err) {
            setSubmissionsError(`Failed to fetch submissions: ${err.message}`);
            console.error('Error fetching submissions:', err);
        } finally {
            setSubmissionsLoading(false);
        }
    };

    // Function to copy table data to clipboard for spreadsheet
    const copyToClipboard = () => {
        const headers = [
            'Name', 'Address', 'ID', 'Builder', 'SqFt', 'Lights', 'Covered',
            'URL', 'Elements', 'Pin Image', 'Photos', 'Latitude', 'Longitude',
            'Group', 'Image Link', 'Google Maps Link'
        ];

        const rows = submissions.map(submission => [
            submission.spotData?.name || '',
            submission.spotData?.address || '',
            submission.spotData?.id || '',
            submission.spotData?.builder || '',
            submission.spotData?.sqft || '',
            submission.spotData?.lights || '',
            submission.spotData?.covered || '',
            submission.spotData?.url || '',
            submission.spotData?.elements || '',
            submission.spotData?.pinimage || '',
            submission.originalFileName || '',
            submission.coordinates?.latitude || submission.spotData?.latitude || '',
            submission.coordinates?.longitude || submission.spotData?.longitude || '',
            submission.spotData?.group || '',
            submission.imageUrl || '',
            submission.coordinates?.googleMapsUrl ||
            (submission.coordinates?.latitude && submission.coordinates?.longitude
                ? `https://www.google.com/maps?q=${submission.coordinates.latitude},${submission.coordinates.longitude}`
                : '')
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${cell}"`).join('\t'))
            .join('\n');

        navigator.clipboard.writeText(csvContent).then(() => {
            alert('Table data copied to clipboard! You can now paste it into a spreadsheet.');
        }).catch(err => {
            console.error('Failed to copy to clipboard:', err);
            alert('Failed to copy to clipboard. Please try selecting and copying manually.');
        });
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handlePhotoUpload = (event) => {
        const files = Array.from(event.target.files);
        setPhotos(files);
        setSubmissionStatus(`${files.length} photo(s) selected`);
    };

    const extractGeoCoordinates = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = function(e) {
                try {
                    // Load the EXIF tags once
                    const tags = ExifReader.load(e.target.result, {expanded: true});

                    // Pass the tags directly to the extraction function
                    const gpsData = extractGPSFromImageTags(tags); // Changed function name

                    console.log('GPS data from extraction:', gpsData);

                    if (gpsData && isValidGPSCoordinates(gpsData)) {
                        resolve({lat: gpsData.latitude, lng: gpsData.longitude});
                    } else {
                        reject('No valid GPS coordinates found');
                    }
                } catch (error) {
                    reject('Error reading EXIF data: ' + error.message);
                }
            };

            reader.readAsArrayBuffer(file);
        });
    };

    function extractGPSFromImageTags(tags) {
        try {
            // Enhanced debugging for Android photos
            console.log('=== DEBUGGING GPS EXTRACTION ===');
            console.log('All available tags:', Object.keys(tags));

            // Log all GPS-related tags for debugging
            console.log('All GPS-related tags found:');
            Object.keys(tags).forEach(key => {
                if (key.toLowerCase().includes('gps') || key.toLowerCase().includes('location')) {
                    console.log(`${key}:`, tags[key]);
                }
            });

            // Check for Android-specific location tags
            console.log('Checking Android-specific tags...');
            const androidTags = ['GPSInfo', 'GPS', 'Location', 'GeoLocation'];
            androidTags.forEach(tag => {
                if (tags[tag]) {
                    console.log(`Found Android tag ${tag}:`, tags[tag]);
                }
            });

            // Check for nested GPS in different structures
            if (tags.exif) {
                console.log('EXIF structure found:', Object.keys(tags.exif));
                if (tags.exif.GPS) {
                    console.log('EXIF.GPS found:', tags.exif.GPS);
                }
            }

            if (tags.ifd0) {
                console.log('IFD0 structure found:', Object.keys(tags.ifd0));
            }

            // Try different possible GPS tag locations
            let gpsData = null;

            // Method 1: Standard GPS tags
            if (tags.GPSLatitude && tags.GPSLongitude && tags.GPSLatitudeRef && tags.GPSLongitudeRef) {
                console.log('Trying standard GPS tags...');
                gpsData = extractFromStandardGPS(tags);
            }

            // Method 2: Check gps sub-object (common in some libraries)
            if (!gpsData && tags.gps) {
                console.log('Trying GPS sub-object...');
                gpsData = extractFromGPSSubObject(tags.gps);
            }

            // Method 3: Check for decimal degree format (some Android apps store it this way)
            if (!gpsData && (tags.GPSLatitude || tags.GPS)) {
                console.log('Trying decimal format...');
                gpsData = extractFromDecimalFormat(tags);
            }

            // Method 4: Check expanded format
            if (!gpsData && tags.exif && tags.exif.GPS) {
                console.log('Trying EXIF GPS sub-object...');
                gpsData = extractFromGPSSubObject(tags.exif.GPS);
            }

            // Method 5: FIXED Android EXIF GPS extraction
            if (!gpsData && tags.exif && tags.exif.GPSLatitude && tags.exif.GPSLongitude) {
                console.log('Trying Android EXIF GPS description fields...');
                console.log('=== DETAILED GPS TAG INSPECTION ===');
                console.log('GPSLatitude full object:', tags.exif.GPSLatitude);
                console.log('GPSLongitude full object:', tags.exif.GPSLongitude);
                console.log('GPSLatitudeRef full object:', tags.exif.GPSLatitudeRef);
                console.log('GPSLongitudeRef full object:', tags.exif.GPSLongitudeRef);

                // Check all properties of each GPS tag
                console.log('GPSLatitude properties:', Object.keys(tags.exif.GPSLatitude));
                console.log('GPSLongitude properties:', Object.keys(tags.exif.GPSLongitude));

                // Log each property's value
                Object.keys(tags.exif.GPSLatitude).forEach(prop => {
                    console.log(`GPSLatitude.${prop}:`, tags.exif.GPSLatitude[prop]);
                });

                Object.keys(tags.exif.GPSLongitude).forEach(prop => {
                    console.log(`GPSLongitude.${prop}:`, tags.exif.GPSLongitude[prop]);
                });

                // Try description fields first (Android often puts readable coordinates here)
                const latTag = tags.exif.GPSLatitude;
                const lngTag = tags.exif.GPSLongitude;
                const latRefTag = tags.exif.GPSLatitudeRef;
                const lngRefTag = tags.exif.GPSLongitudeRef;

                if (latTag.description && lngTag.description) {
                    console.log('Found GPS descriptions:', latTag.description, lngTag.description);

                    const latMatch = latTag.description.match(/[\d.]+/);
                    const lngMatch = lngTag.description.match(/[\d.]+/);

                    if (latMatch && lngMatch) {
                        let lat = parseFloat(latMatch[0]);
                        let lng = parseFloat(lngMatch[0]);

                        // Apply direction
                        if (latTag.description.includes('S') || (latRefTag && latRefTag.description === 'S')) lat = -lat;
                        if (lngTag.description.includes('W') || (lngRefTag && lngRefTag.description === 'W')) lng = -lng;

                        gpsData = { latitude: lat, longitude: lng };
                        console.log('Successfully extracted from Android descriptions:', gpsData);
                    }
                }
            }

            if (gpsData) {
                // Validate coordinates
                if (isNaN(gpsData.latitude) || isNaN(gpsData.longitude) ||
                    gpsData.latitude < -90 || gpsData.latitude > 90 ||
                    gpsData.longitude < -180 || gpsData.longitude > 180 ||
                    (gpsData.latitude === 0 && gpsData.longitude === 0)) {
                    console.log('Invalid coordinates detected:', gpsData);
                    return null;
                }

                console.log('Valid GPS coordinates found:', gpsData);
                return gpsData;
            }

            console.log('No valid GPS coordinates found in any format');
            return null;
        } catch (error) {
            console.error('Error extracting GPS data:', error);
            return null;
        }
    }

    function extractFromStandardGPS(tags) {
        try {
            let lat = convertDMSToDD(tags.GPSLatitude.value, tags.GPSLatitudeRef.value);
            let lng = convertDMSToDD(tags.GPSLongitude.value, tags.GPSLongitudeRef.value);

            if (!isNaN(lat) && !isNaN(lng)) {
                return { latitude: lat, longitude: lng };
            }
        } catch (error) {
            console.log('Standard GPS extraction failed:', error);
        }
        return null;
    }

    function extractFromGPSSubObject(gpsObj) {
        try {
            // Check for simple decimal coordinates first (common format)
            if (gpsObj.Latitude && gpsObj.Longitude &&
                typeof gpsObj.Latitude === 'number' && typeof gpsObj.Longitude === 'number') {
                console.log('Found simple decimal GPS coordinates:', gpsObj.Latitude, gpsObj.Longitude);
                return { latitude: gpsObj.Latitude, longitude: gpsObj.Longitude };
            }

            // Check for different property names with complex structures
            const latProps = ['Latitude', 'latitude', 'GPSLatitude'];
            const lngProps = ['Longitude', 'longitude', 'GPSLongitude'];
            const latRefProps = ['LatitudeRef', 'latitudeRef', 'GPSLatitudeRef'];
            const lngRefProps = ['LongitudeRef', 'longitudeRef', 'GPSLongitudeRef'];

            let lat, lng, latRef, lngRef;

            // Find latitude
            for (const prop of latProps) {
                if (gpsObj[prop]) {
                    lat = gpsObj[prop].value || gpsObj[prop];
                    break;
                }
            }

            // Find longitude
            for (const prop of lngProps) {
                if (gpsObj[prop]) {
                    lng = gpsObj[prop].value || gpsObj[prop];
                    break;
                }
            }

            // Find latitude reference
            for (const prop of latRefProps) {
                if (gpsObj[prop]) {
                    latRef = gpsObj[prop].value || gpsObj[prop];
                    break;
                }
            }

            // Find longitude reference
            for (const prop of lngRefProps) {
                if (gpsObj[prop]) {
                    lngRef = gpsObj[prop].value || gpsObj[prop];
                    break;
                }
            }

            if (lat && lng && latRef && lngRef) {
                let latDecimal = convertDMSToDD(lat, latRef);
                let lngDecimal = convertDMSToDD(lng, lngRef);

                if (!isNaN(latDecimal) && !isNaN(lngDecimal)) {
                    return { latitude: latDecimal, longitude: lngDecimal };
                }
            }
        } catch (error) {
            console.log('GPS sub-object extraction failed:', error);
        }
        return null;
    }

    function extractFromDecimalFormat(tags) {
        try {
            // Some Android apps store GPS as decimal degrees directly
            let lat, lng;

            // Check various possible decimal degree formats
            if (tags.GPSLatitude && typeof tags.GPSLatitude.value === 'number') {
                lat = tags.GPSLatitude.value;
            } else if (tags.GPSLatitude && tags.GPSLatitude.description && !isNaN(parseFloat(tags.GPSLatitude.description))) {
                lat = parseFloat(tags.GPSLatitude.description);
            }

            if (tags.GPSLongitude && typeof tags.GPSLongitude.value === 'number') {
                lng = tags.GPSLongitude.value;
            } else if (tags.GPSLongitude && tags.GPSLongitude.description && !isNaN(parseFloat(tags.GPSLongitude.description))) {
                lng = parseFloat(tags.GPSLongitude.description);
            }

            // Apply direction references if available
            if (lat && lng) {
                if (tags.GPSLatitudeRef && (tags.GPSLatitudeRef.value === 'S' || tags.GPSLatitudeRef.value === ['S'])) {
                    lat = -Math.abs(lat);
                }
                if (tags.GPSLongitudeRef && (tags.GPSLongitudeRef.value === 'W' || tags.GPSLongitudeRef.value === ['W'])) {
                    lng = -Math.abs(lng);
                }

                return { latitude: lat, longitude: lng };
            }
        } catch (error) {
            console.log('Decimal format extraction failed:', error);
        }
        return null;
    }

    function convertDMSToDD(dms, ref) {
        console.log('Converting DMS:', dms, 'Ref:', ref, 'Ref type:', typeof ref);

        // Validate inputs first
        if (!dms || (!ref && ref !== '')) {
            console.error('Missing DMS or reference data');
            return NaN;
        }

        // Handle different possible formats
        let degrees, minutes, seconds;

        if (Array.isArray(dms) && dms.length >= 3) {
            // EXIF format: array of [numerator, denominator] pairs
            try {
                // Handle potential zero denominators or invalid data
                degrees = (dms[0] && dms[0][1] !== 0) ? dms[0][0] / dms[0][1] : 0;
                minutes = (dms[1] && dms[1][1] !== 0) ? dms[1][0] / dms[1][1] : 0;
                seconds = (dms[2] && dms[2][1] !== 0) ? dms[2][0] / dms[2][1] : 0;

                // Check for division by zero or invalid values
                if (!isFinite(degrees) || !isFinite(minutes) || !isFinite(seconds)) {
                    console.error('Invalid DMS values after division:', { degrees, minutes, seconds });

                    // Try alternative: maybe it's already decimal degrees in the first element
                    if (dms[0] && dms[0][1] !== 0) {
                        const decimalValue = dms[0][0] / dms[0][1];
                        if (isFinite(decimalValue) && decimalValue > 0 && decimalValue < 180) {
                            console.log('Using decimal value from first element:', decimalValue);
                            return decimalValue; // Return positive, we'll handle direction below
                        }
                    }

                    return NaN;
                }
            } catch (error) {
                console.error('Error parsing DMS array:', error);
                return NaN;
            }
        } else {
            console.error('Unexpected DMS format:', typeof dms, dms);
            return NaN;
        }

        console.log('Parsed DMS values:', { degrees, minutes, seconds });

        // Convert to decimal degrees
        let dd = degrees + (minutes / 60) + (seconds / 3600);
        console.log('Decimal degrees before direction:', dd);

        // Handle direction reference - Android sometimes has empty arrays
        let direction = null;
        if (Array.isArray(ref)) {
            if (ref.length > 0) {
                direction = ref[0];
            } else {
                console.log('Empty reference array - assuming positive coordinate');
                return dd; // Return as-is, assuming positive
            }
        } else if (typeof ref === 'string') {
            direction = ref;
        } else if (ref && ref.value) {
            direction = ref.value;
        }

        console.log('Reference direction after extraction:', direction);

        // Apply direction if we have it
        if (direction && ['S', 'W'].includes(direction)) {
            dd = dd * -1;
            console.log('Applied negative for', direction, 'result:', dd);
        } else {
            console.log('No negative applied, result:', dd);
        }

        console.log('Final decimal degrees:', dd);
        return dd;
    }

    const isValidGPSCoordinates = (gpsData) => {
        if (!gpsData ||
            gpsData.latitude === undefined ||
            gpsData.longitude === undefined ||
            isNaN(gpsData.latitude) ||
            isNaN(gpsData.longitude)) {
            return false;
        }

        // Check valid coordinate ranges
        if (gpsData.latitude < -90 || gpsData.latitude > 90 ||
            gpsData.longitude < -180 || gpsData.longitude > 180) {
            return false;
        }

        // Reject coordinates that are exactly (0,0) or very close to it
        // This catches many cases of invalid/missing GPS data
        if (Math.abs(gpsData.latitude) < 0.001 && Math.abs(gpsData.longitude) < 0.001) {
            console.warn('GPS coordinates too close to (0,0), likely invalid');
            return false;
        }

        return true;
    };

    const validatePhotos = async (files) => {
        const validatedPhotos = [];
        const errors = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            try {
                const coords = await extractGeoCoordinates(file);
                validatedPhotos.push({
                    file: file,
                    coordinates: coords,
                    name: file.name
                });
            } catch (error) {
                errors.push(`${file.name}: ${error}`);
            }
        }

        return { validatedPhotos, errors };
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (photos.length === 0) {
            setSubmissionStatus("Please select at least one photo to upload");
            return;
        }

        setSubmissionStatus("Validating photos for GPS coordinates...");

        try {
            const { validatedPhotos, errors } = await validatePhotos(photos);

            if (validatedPhotos.length === 0) {
                setSubmissionStatus(`No valid photos found. All photos must contain GPS coordinates. Errors: ${errors.join(', ')}`);
                return;
            }

            if (errors.length > 0) {
                console.warn("Some photos rejected:", errors);
                setSubmissionStatus(`Warning: ${errors.length} photo(s) rejected for missing GPS data. Proceeding with ${validatedPhotos.length} valid photo(s)...`);
            }

            setSubmissionStatus(`Uploading ${validatedPhotos.length} photo(s)...`);

            const uploadData = new FormData();

            validatedPhotos.forEach((photoData, index) => {
                uploadData.append(`photos`, photoData.file);

                const metadata = {
                    ...formData,
                    coordinates: photoData.coordinates,
                    originalFileName: photoData.name,
                    uploadIndex: index
                };

                uploadData.append(`metadata_${index}`, JSON.stringify(metadata));
            });

            uploadData.append('spotData', JSON.stringify(formData));
            uploadData.append('photoCount', validatedPhotos.length.toString());

            const response = await fetch(process.env.REACT_APP_SUBMISSION_API_URL, {
                method: "POST",
                body: uploadData
            });

            // Parse the response
            const responseData = await response.json();

            if (response.ok && responseData.success) {
                // Success case
                console.log("Submission successful!");
                setSubmissionStatus(`Successfully uploaded ${responseData.processed} photo(s)! Thank you for your submission.`);

                // Reset form
                setFormData({
                    name: '',
                    address: '',
                    id: '',
                    builder: '',
                    sqft: '',
                    lights: '',
                    covered: '',
                    url: '',
                    elements: '',
                    pinimage: '',
                    group: ''
                });
                setPhotos([]);

                const fileInput = document.getElementById('photos');
                if (fileInput) fileInput.value = '';

                // Refresh submissions list after successful upload
                fetchSubmissions();

            } else {
                // Server returned an error response
                console.error("Submission failed with server error:", responseData);

                let errorMessage = "Submission failed";

                if (responseData.error) {
                    errorMessage = responseData.error;
                } else if (responseData.message) {
                    errorMessage = responseData.message;
                } else if (responseData.errors && responseData.errors.length > 0) {
                    errorMessage = `Upload failed: ${responseData.errors.join(', ')}`;
                }

                // Show specific error about GPS data if no photos were processed
                if (responseData.processed === 0 && responseData.total > 0) {
                    errorMessage = `No photos could be uploaded. All ${responseData.total} photo(s) are missing GPS coordinates. Please ensure location services are enabled in your camera app and take photos outdoors with clear GPS signal.`;
                }

                setSubmissionStatus(errorMessage);
            }

        } catch (error) {
            console.error("Network or parsing error:", error);
            setSubmissionStatus("Network error occurred during submission. Please check your connection and try again.");
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit}>
                <div id="skateparkinputform" style={{maxWidth: '600px'}}>
                    {/* Main Section - Prominent */}
                    <div style={{marginBottom: '20px', padding: '20px', border: '2px solid #007cba', borderRadius: '8px', backgroundColor: '#f8f9fa'}}>
                        <h3 style={{margin: '0 0 15px 0', color: '#007cba'}}>Upload Skate Spot Photos</h3>
                        <div style={{marginBottom: '15px', padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px'}}>
                            <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: '#856404'}}>📍 GPS Coordinates Required</p>
                            <p style={{margin: '0 0 8px 0', fontSize: '13px', color: '#856404'}}>
                                <strong>Important:</strong> Photos must be taken with a camera app that saves GPS location data.
                            </p>
                            <p style={{margin: '0 0 8px 0', fontSize: '12px', color: '#856404'}}>
                                • Enable location services in your camera app<br/>
                                • Take photos outdoors for best GPS signal<br/>
                                • <strong>Select photos from your gallery/camera roll</strong>
                            </p>
                            <p style={{margin: '0', fontSize: '12px', color: '#d63384', fontWeight: 'bold'}}>
                                ⚠️ DO NOT use "Take Photo" option - web photos lack GPS data
                            </p>
                        </div>

                        <div style={{marginBottom: '15px'}}>
                            <label htmlFor="photos" style={{display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '16px'}}>
                                Select Photos from Gallery*:
                            </label>
                            <input
                                onChange={handlePhotoUpload}
                                type="file"
                                id="photos"
                                name="photos"
                                accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                                multiple
                                required
                                style={{padding: '8px', fontSize: '14px', width: '100%', maxWidth: '400px'}}
                            />
                        </div>

                        <div style={{padding: "10px", backgroundColor: "#e9ecef", border: "1px solid #dee2e6", borderRadius: "4px", marginBottom: '15px'}}>
                            <strong>Status:</strong> <span>{submissionStatus}</span>
                        </div>

                        <input
                            id="mySubmit"
                            name="mySubmit"
                            type="submit"
                            value="Upload Photos & Submit Spot"
                            style={{
                                padding: '12px 24px',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                backgroundColor: '#007cba',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        />
                    </div>

                    {/* Optional Fields - Less Prominent */}
                    <div style={{marginBottom: '20px'}}>
                        <button
                            type="button"
                            onClick={() => setShowOptionalFields(!showOptionalFields)}
                            style={{
                                background: 'none',
                                border: '1px solid #ccc',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#666'
                            }}
                        >
                            {showOptionalFields ? '∧ Hide' : '+ Show'} Optional Details
                        </button>
                        <span style={{marginLeft: '10px', fontSize: '13px', color: '#888'}}>
                            (spot name, address, features, etc.)
                        </span>
                    </div>

                    {showOptionalFields && (
                        <div style={{padding: '15px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px'}}>
                            <h4 style={{margin: '0 0 15px 0', fontSize: '16px', color: '#666'}}>Optional Information</h4>

                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px'}}>
                                <div>
                                    <label htmlFor="name" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Spot Name:</label>
                                    <input onChange={handleInputChange} type="text" id="name" name="name" placeholder="skatepark/spot name" value={formData.name} style={{width: '100%', padding: '6px', fontSize: '13px'}} />
                                </div>
                                <div>
                                    <label htmlFor="address" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Address:</label>
                                    <input onChange={handleInputChange} type="text" id="address" name="address" placeholder="nearest address" value={formData.address} style={{width: '100%', padding: '6px', fontSize: '13px'}} />
                                </div>
                            </div>

                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px'}}>
                                <div>
                                    <label htmlFor="elements" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Features:</label>
                                    <input onChange={handleInputChange} type="text" id="elements" name="elements" placeholder="transition, street, bowl, etc." value={formData.elements} style={{width: '100%', padding: '6px', fontSize: '13px'}} />
                                </div>
                                <div>
                                    <label htmlFor="builder" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Builder:</label>
                                    <input onChange={handleInputChange} type="text" id="builder" name="builder" placeholder="builder and/or designer" value={formData.builder} style={{width: '100%', padding: '6px', fontSize: '13px'}} />
                                </div>
                            </div>

                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px'}}>
                                <div>
                                    <label htmlFor="sqft" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Size (sq ft):</label>
                                    <input onChange={handleInputChange} type="text" id="sqft" name="sqft" placeholder="approximate size" value={formData.sqft} style={{width: '100%', padding: '6px', fontSize: '13px'}} />
                                </div>
                                <div>
                                    <label htmlFor="lights" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Lights:</label>
                                    <input onChange={handleInputChange} type="text" id="lights" name="lights" placeholder="yes/no/partial" value={formData.lights} style={{width: '100%', padding: '6px', fontSize: '13px'}} />
                                </div>
                                <div>
                                    <label htmlFor="covered" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Covered:</label>
                                    <input onChange={handleInputChange} type="text" id="covered" name="covered" placeholder="yes/no/partial" value={formData.covered} style={{width: '100%', padding: '6px', fontSize: '13px'}} />
                                </div>
                            </div>

                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px'}}>
                                <div>
                                    <label htmlFor="url" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Website:</label>
                                    <input onChange={handleInputChange} type="text" id="url" name="url" placeholder="website or social link" value={formData.url} style={{width: '100%', padding: '6px', fontSize: '13px'}} />
                                </div>
                                <div>
                                    <label htmlFor="pinimage" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Pin Style:</label>
                                    <select id="pinimage" name="pinimage" onChange={handleInputChange} value={formData.pinimage} style={{width: '100%', padding: '6px', fontSize: '13px'}}>
                                        <option value="">Select pin image</option>
                                        {uniquePin.map((pin, index) => (
                                            <option key={index} value={pin}>{pin}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="group" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Location Group:</label>
                                    <select id="group" name="group" onChange={handleInputChange} value={formData.group} style={{width: '100%', padding: '6px', fontSize: '13px'}}>
                                        <option value="">Select location group</option>
                                        {uniqueLocationGroup.map((code, index) => (
                                            <option key={index} value={code}>{code}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={{marginTop: '10px'}}>
                                <label htmlFor="id" style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '3px'}}>Spot ID:</label>
                                <input onChange={handleInputChange} type="text" id="id" name="id" placeholder="unique spot identifier" value={formData.id} style={{width: '100%', maxWidth: '200px', padding: '6px', fontSize: '13px'}} />
                            </div>
                        </div>
                    )}

                    {/* Submissions History Section */}
                    <div style={{marginBottom: '20px'}}>
                        <button
                            type="button"
                            onClick={() => setShowSubmissions(!showSubmissions)}
                            style={{
                                background: '#28a745',
                                border: 'none',
                                padding: '10px 16px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        >
                            {showSubmissions ? '- Hide' : '+ Show'} Submission History ({submissions.length})
                        </button>
                        {showSubmissions && (
                            <div style={{marginTop: '15px', padding: '20px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px'}}>
                                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0, color: '#007cba' }}>
                                        Submission History ({submissions.length} submissions)
                                    </h3>
                                    <div>
                                        <button
                                            onClick={fetchSubmissions}
                                            style={{
                                                padding: '8px 16px',
                                                backgroundColor: '#28a745',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                marginRight: '10px',
                                                fontSize: '12px'
                                            }}
                                            disabled={submissionsLoading}
                                        >
                                            {submissionsLoading ? 'Loading...' : 'Refresh'}
                                        </button>
                                        <button
                                            onClick={copyToClipboard}
                                            disabled={submissions.length === 0}
                                            style={{
                                                padding: '8px 16px',
                                                backgroundColor: submissions.length > 0 ? '#17a2b8' : '#6c757d',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: submissions.length > 0 ? 'pointer' : 'not-allowed',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Copy to Clipboard
                                        </button>
                                    </div>
                                </div>

                                {submissionsLoading ? (
                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '16px', color: '#666' }}>Loading submissions...</div>
                                    </div>
                                ) : submissionsError ? (
                                    <div style={{
                                        backgroundColor: '#f8d7da',
                                        color: '#721c24',
                                        padding: '12px',
                                        borderRadius: '4px',
                                        marginBottom: '10px'
                                    }}>
                                        {submissionsError}
                                    </div>
                                ) : submissions.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '40px',
                                        backgroundColor: 'white',
                                        borderRadius: '4px',
                                        color: '#666'
                                    }}>
                                        No submissions found.
                                    </div>
                                ) : (
                                    <div>
                                        {/* Desktop Table */}
                                        <div className="desktop-table">
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{
                                                    width: '100%',
                                                    borderCollapse: 'collapse',
                                                    fontSize: '11px',
                                                    backgroundColor: 'white',
                                                    border: '1px solid #dee2e6'
                                                }}>
                                                    <thead>
                                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Name</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Address</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>ID</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Builder</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>SqFt</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Lights</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Covered</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>URL</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Elements</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Pin Image</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Photos</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Latitude</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Longitude</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Group</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Image Link</th>
                                                        <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>Google Maps</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {submissions.map((submission, index) => (
                                                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.name || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.address || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.id || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.builder || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.sqft || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.lights || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.covered || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>
                                                                {submission.spotData?.url ? (
                                                                    <a href={submission.spotData.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007cba' }}>
                                                                        {submission.spotData.url.length > 20 ? submission.spotData.url.substring(0, 20) + '...' : submission.spotData.url}
                                                                    </a>
                                                                ) : ''}
                                                            </td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.elements || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.pinimage || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.originalFileName || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>
                                                                {submission.coordinates?.latitude || submission.spotData?.latitude || ''}
                                                            </td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>
                                                                {submission.coordinates?.longitude || submission.spotData?.longitude || ''}
                                                            </td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>{submission.spotData?.group || ''}</td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>
                                                                {submission.imageUrl ? (
                                                                    <a href={submission.imageUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#007cba' }}>
                                                                        View
                                                                    </a>
                                                                ) : ''}
                                                            </td>
                                                            <td style={{ border: '1px solid #dee2e6', padding: '4px' }}>
                                                                {(submission.coordinates?.googleMapsUrl ||
                                                                    (submission.coordinates?.latitude && submission.coordinates?.longitude &&
                                                                        `https://www.google.com/maps?q=${submission.coordinates.latitude},${submission.coordinates.longitude}`)) ? (
                                                                    <a
                                                                        href={submission.coordinates?.googleMapsUrl ||
                                                                        `https://www.google.com/maps?q=${submission.coordinates.latitude},${submission.coordinates.longitude}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ color: '#007cba' }}
                                                                    >
                                                                        Map
                                                                    </a>
                                                                ) : ''}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Mobile Cards */}
                                        <div className="mobile-cards" style={{ display: 'none' }}>
                                            {submissions.map((submission, index) => (
                                                <div key={index} style={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #dee2e6',
                                                    borderRadius: '4px',
                                                    padding: '12px',
                                                    marginBottom: '10px',
                                                    fontSize: '12px'
                                                }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#007cba' }}>
                                                        {submission.spotData?.name || 'Unnamed Spot'}
                                                    </div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                        <div><strong>Address:</strong> {submission.spotData?.address || 'N/A'}</div>
                                                        <div><strong>ID:</strong> {submission.spotData?.id || 'N/A'}</div>
                                                        <div><strong>Builder:</strong> {submission.spotData?.builder || 'N/A'}</div>
                                                        <div><strong>Elements:</strong> {submission.spotData?.elements || 'N/A'}</div>
                                                        <div><strong>Lights:</strong> {submission.spotData?.lights || 'N/A'}</div>
                                                        <div><strong>Covered:</strong> {submission.spotData?.covered || 'N/A'}</div>
                                                        <div style={{ gridColumn: '1 / -1' }}><strong>Photo:</strong> {submission.originalFileName || 'N/A'}</div>
                                                        <div style={{ gridColumn: '1 / -1' }}>
                                                            <strong>Links:</strong>
                                                            {submission.imageUrl && (
                                                                <a href={submission.imageUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#007cba', marginLeft: '8px' }}>
                                                                    View Image
                                                                </a>
                                                            )}
                                                            {(submission.coordinates?.latitude && submission.coordinates?.longitude) && (
                                                                <a
                                                                    href={`https://www.google.com/maps?q=${submission.coordinates.latitude},${submission.coordinates.longitude}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{ color: '#007cba', marginLeft: '8px' }}
                                                                >
                                                                    Maps
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div style={{
                                    marginTop: '15px',
                                    padding: '10px',
                                    backgroundColor: 'white',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    color: '#666'
                                }}>
                                    <strong>Note:</strong> Click "Copy to Clipboard" to copy all data in tab-separated format that can be pasted directly into Excel or Google Sheets.
                                    The data will include headers and be properly formatted for spreadsheet import.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </form>
        </>
    );
}

export default SkateparkInputForm;